// Care plans, care task list, caregiver skills checklist and the task catalog.
// Data rules live in the database (care_plan_draft / _sign / _activate /
// _review); this file is the query layer plus a few labels.
import { supabase } from './supabase';
import { CLOSED_STATUS_FILTER } from './serviceModel';

export const FREQUENCIES = [
  ['every_visit', 'Every visit'], ['daily', 'Daily'], ['weekly', 'Weekly (choose days)'], ['as_needed', 'As needed'],
];
export const GOAL_KINDS = [['skilled', 'Skilled nursing'], ['pss', 'Personal support (ADLs)'], ['teaching', 'Teaching plan']];
export const GOAL_STATUSES = [['active', 'Active'], ['progressing', 'Progressing'], ['met', 'Met'], ['not_met', 'Not met'], ['discontinued', 'Discontinued']];
export const TASK_KINDS = [['adl', 'ADL'], ['iadl', 'IADL'], ['observation', 'Observation'], ['treatment', 'Treatment'], ['skilled', 'Skilled'], ['log', 'Log / note']];
export const SKILL_KINDS = [['pss', 'PSS / aide'], ['skilled', 'Skilled (RN / LPN)'], ['either', 'Either']];
export const CAREGIVER_ROLES = [['family', 'Family caregiver'], ['cna', 'CNA / aide'], ['lpn', 'LPN'], ['rn', 'RN'], ['other', 'Other']];
export const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// ── Task catalog ─────────────────────────────────────────────────────────────
export const getTaskCatalog = (includeInactive = false) => {
  let q = supabase.from('task_catalog').select('*').order('sort_order').order('name');
  if (!includeInactive) q = q.eq('active', true);
  return q;
};
export const saveTask = (task) => task.id
  ? supabase.from('task_catalog').update(task).eq('id', task.id).select().single()
  : supabase.from('task_catalog').insert([task]).select().single();

// ── Board ────────────────────────────────────────────────────────────────────
export const getCarePlanBoard = async () => {
  const [{ data: clients, error: e1 }, { data: plans, error: e2 }, { data: assessments }, { data: skills }, { data: tasks }] = await Promise.all([
    supabase.from('clients')
      .select('id, client_id, first_name, last_name, current_status, client_status_zone, county, pa_expiry_date, nurse_assessment_date, responsible_person_name')
      .not('current_status', 'in', CLOSED_STATUS_FILTER)
      .order('last_name'),
    supabase.from('care_plans').select('id, client_id, version_number, status, effective_date, review_due, reviewed_at, rn_signed_at, caregiver_signed_at, based_on_assessment_id')
      .in('status', ['draft', 'active']).order('version_number', { ascending: false }),
    supabase.from('nurse_assessments').select('id, client_uuid, assessment_date').order('assessment_date', { ascending: false }),
    supabase.from('caregiver_skills').select('client_id, independent_on, verified_at'),
    supabase.from('care_plan_tasks').select('care_plan_id'),
  ]);
  if (e1) throw new Error(e1.message);
  if (e2) throw new Error(e2.message);
  const byClient = {};
  (plans || []).forEach(p => { const b = byClient[p.client_id] = byClient[p.client_id] || {}; if (p.status === 'active' && !b.active) b.active = p; if (p.status === 'draft') b.draft = p; });
  const latestAssessment = {};
  (assessments || []).forEach(a => { if (!latestAssessment[a.client_uuid]) latestAssessment[a.client_uuid] = a; });
  const skillsByClient = {};
  (skills || []).forEach(s => { const b = skillsByClient[s.client_id] = skillsByClient[s.client_id] || { total: 0, done: 0 }; b.total += 1; if (s.independent_on) b.done += 1; });
  const tasksByPlan = {};
  (tasks || []).forEach(t => { tasksByPlan[t.care_plan_id] = (tasksByPlan[t.care_plan_id] || 0) + 1; });
  return (clients || []).map(c => {
    const b = byClient[c.id] || {};
    const current = b.active || b.draft || null;
    return { ...c, activePlan: b.active || null, draftPlan: b.draft || null, plan: current,
      assessment: latestAssessment[c.id] || null, skills: skillsByClient[c.id] || { total: 0, done: 0 },
      taskCount: current ? (tasksByPlan[current.id] || 0) : 0, planStatus: planStatusOf(b.active, b.draft, latestAssessment[c.id]) };
  });
};

// Draft → Active → Review due (14 days) → Overdue; No plan; Assessment newer than plan.
export const planStatusOf = (active, draft, assessment) => {
  const today = new Date().toISOString().slice(0, 10);
  if (!active && !draft) return { key: 'none', label: 'No plan', tone: 'danger' };
  if (!active && draft) return { key: 'draft', label: 'Draft', tone: 'warn' };
  if (assessment?.assessment_date && active.effective_date && assessment.assessment_date > active.effective_date && !draft) {
    return { key: 'stale', label: 'Newer assessment', tone: 'warn' };
  }
  if (active.review_due && active.review_due < today) return { key: 'overdue', label: 'Review overdue', tone: 'danger' };
  if (active.review_due) {
    const days = Math.round((new Date(active.review_due) - new Date(today)) / 86400000);
    if (days <= 14) return { key: 'due', label: `Review in ${days}d`, tone: 'warn' };
  }
  return { key: 'active', label: draft ? 'Active · draft pending' : 'Active', tone: 'success' };
};

// ── One client's plans ───────────────────────────────────────────────────────
export const getClientPlans = (clientId) =>
  supabase.from('care_plans').select('*, rn:profiles!care_plans_rn_signed_by_fkey ( full_name )').eq('client_id', clientId).order('version_number', { ascending: false });

export const getPlanDetail = async (plan) => {
  const [{ data: goals }, { data: tasks }, { data: skills }] = await Promise.all([
    supabase.from('care_plan_goals').select('*').eq('care_plan_id', plan.id).order('sort_order').order('updated_at'),
    supabase.from('care_plan_tasks').select('*, task:task_catalog ( id, code, name, category, kind, skill_kind, requires_competency, instructions )').eq('care_plan_id', plan.id).order('sort_order'),
    supabase.from('caregiver_skills').select('*, staff:staff ( id, first_name, last_name, position_code )').eq('client_id', plan.client_id).or(`care_plan_id.eq.${plan.id},care_plan_id.is.null`).order('created_at'),
  ]);
  return { goals: goals || [], tasks: (tasks || []).sort((a, b) => (a.task?.sort_order ?? 0) - (b.task?.sort_order ?? 0) || a.sort_order - b.sort_order), skills: skills || [] };
};

export const draftPlan = async (clientId, assessmentId = null) => {
  const { data, error } = await supabase.rpc('care_plan_draft', { p_client_id: clientId, p_assessment_id: assessmentId });
  if (error) throw new Error(error.message);
  return data;
};
export const updatePlan = (id, patch) => supabase.from('care_plans').update(patch).eq('id', id);
export const signPlan = async (planId, who, name, role) => {
  const { error } = await supabase.rpc('care_plan_sign', { p_plan_id: planId, p_who: who, p_name: name || null, p_role: role || null });
  if (error) throw new Error(error.message);
};
export const activatePlan = async (planId) => {
  const { data, error } = await supabase.rpc('care_plan_activate', { p_plan_id: planId });
  if (error) throw new Error(error.message);
  return data;
};
export const reviewPlan = async (planId, nextDue, note) => {
  const { error } = await supabase.rpc('care_plan_review', { p_plan_id: planId, p_next_due: nextDue || null, p_note: note || null });
  if (error) throw new Error(error.message);
};
export const archivePlan = (id) => supabase.from('care_plans').update({ status: 'archived' }).eq('id', id).eq('status', 'draft');

export const upsertGoal = (g) => g.id
  ? supabase.from('care_plan_goals').update({ ...g, updated_at: new Date().toISOString() }).eq('id', g.id).select().single()
  : supabase.from('care_plan_goals').insert([g]).select().single();
export const deleteGoal = (id) => supabase.from('care_plan_goals').delete().eq('id', id);

export const upsertPlanTask = (t) => t.id
  ? supabase.from('care_plan_tasks').update(t).eq('id', t.id).select().single()
  : supabase.from('care_plan_tasks').insert([t]).select().single();
export const deletePlanTask = (id) => supabase.from('care_plan_tasks').delete().eq('id', id);

export const upsertSkill = (s) => s.id
  ? supabase.from('caregiver_skills').update({ ...s, updated_at: new Date().toISOString() }).eq('id', s.id).select().single()
  : supabase.from('caregiver_skills').insert([s]).select().single();
export const deleteSkill = (id) => supabase.from('caregiver_skills').delete().eq('id', id);

export const getFieldStaffForSkills = () =>
  supabase.from('staff').select('id, first_name, last_name, position_code, status').eq('status', 'active').order('last_name');

export const skillStage = (s) => s.independent_on ? 'Independent' : s.with_assistance_on ? 'With assistance' : s.return_demo_on ? 'Return demo' : s.demonstrated_on ? 'Demonstrated' : 'Not started';
